import { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { api } from '../api';
import TabAgrupamiento  from './TabAgrupamiento';
import TabEquivalencias from './TabEquivalencias';
import TabOptimizacion  from './TabOptimizacion';

const CATEGORIAS = [
  { key: 'filtros',  label: '🔧 Filtros',   color: '#4cc9f0' },
  { key: 'galones',  label: '🛢️ Galones',   color: '#f72585' },
  { key: 'litros',   label: '🧴 Litros',    color: '#7209b7' },
  { key: 'canecas',  label: '🪣 Canecas',   color: '#f4a261' },
  { key: 'varios',   label: '📦 Varios',    color: '#4361ee' },
];

// ─── color según margen % ───
function colorMargen(pct) {
  if (pct >= 40) return '#4caf50';
  if (pct >= 20) return '#ffc107';
  if (pct >= 5)  return '#ff9800';
  return '#e63946';
}
function bgMargen(pct) {
  if (pct >= 40) return '#1a3a2a';
  if (pct >= 20) return '#3a2e10';
  if (pct >= 5)  return '#3a2a10';
  return '#3a1a1a';
}

// ─── Componente tabla ventas/compras ───
function TablaTop({ datos, color, esCompra }) {
  if (!datos || datos.productos.length === 0) return (
    <p style={{ color: '#555', textAlign: 'center', padding: 24 }}>Sin datos para este período</p>
  );

  const maxVal = datos.productos[0]?.[esCompra ? 'total_comprado' : 'total_vendido'] || 1;
  const campoQty   = esCompra ? 'total_comprado' : 'total_vendido';
  const campoCosto = esCompra ? 'costo_total' : 'revenue';
  const labelQty   = esCompra ? 'Comprado' : 'Vendido';
  const labelCosto = esCompra ? 'Costo total' : 'Revenue';

  return (
    <div>
      <div style={s.marcaLider}>
        🏆 Marca líder: <strong style={{ color }}>{datos.marca_lider}</strong>
        <span style={s.marcaTotal}>
          · {datos.productos.reduce((a, p) => a + p[campoQty], 0).toFixed(0)} unidades
          · ${datos.productos.reduce((a, p) => a + p[campoCosto], 0).toFixed(2)} {labelCosto.toLowerCase()}
        </span>
      </div>

      <ResponsiveContainer width="100%" height={200}>
        <BarChart data={datos.productos} layout="vertical" margin={{ left: 0, right: 20 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#2a2a3e" horizontal={false} />
          <XAxis type="number" stroke="#aaa" fontSize={11} />
          <YAxis type="category" dataKey="marca" stroke="#aaa" fontSize={10} width={70} />
          <Tooltip
            contentStyle={{ background: '#1e1e2e', border: 'none', color: '#fff', fontSize: 12 }}
            formatter={(val) => [`${val} uds`, labelQty]}
          />
          <Bar dataKey={campoQty} fill={color} radius={[0, 4, 4, 0]} />
        </BarChart>
      </ResponsiveContainer>

      <div style={s.tabla}>
        <div style={s.tablaHeader}>
          <span style={{ flex: 3 }}>PRODUCTO</span>
          <span style={{ width: 80, textAlign: 'right' }}>{labelQty.toUpperCase()}</span>
          <span style={{ width: 80, textAlign: 'right' }}>STOCK</span>
          <span style={{ width: 100, textAlign: 'right' }}>{labelCosto.toUpperCase()}</span>
        </div>
        {datos.productos.map((p, i) => (
          <div key={i} style={s.tablaRow}>
            <div style={{ flex: 3 }}>
              <div style={s.itemNombre}>{p.item}</div>
              <div style={s.itemCodigo}>{p.codigo} · {p.marca}</div>
            </div>
            <div style={{ width: 80, textAlign: 'right' }}>
              <div style={s.barraContainer}>
                <div style={{ ...s.barraFill, width: `${(p[campoQty] / maxVal) * 100}%`, background: color }} />
              </div>
              <span style={{ color: '#fff', fontSize: 13, fontWeight: 'bold' }}>{p[campoQty].toFixed(0)}</span>
            </div>
            <span style={{ width: 80, textAlign: 'right', color: p.stock_actual < 5 ? '#f44336' : '#aaa', fontSize: 13 }}>
              {p.stock_actual.toFixed(0)}{p.stock_actual < 5 && ' ⚠️'}
            </span>
            <span style={{ width: 100, textAlign: 'right', color: '#4cc9f0', fontSize: 13 }}>${p[campoCosto].toFixed(2)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Componente tabla rentabilidad ───
function TablaRentabilidad({ datos, categoriaActiva, setCategoriaActiva }) {
  if (!datos) return <p style={{ color: '#aaa', padding: 40, textAlign: 'center' }}>Cargando rentabilidad...</p>;

  const cat       = CATEGORIAS.find(c => c.key === categoriaActiva);
  const dataCat   = datos[categoriaActiva];
  const kpis      = datos.kpis_globales;
  const kpisCat   = dataCat?.kpis;
  const productos = dataCat?.productos || [];

  return (
    <div>
      {/* KPIs globales */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        {[
          { label: 'Revenue total',   valor: `$${kpis?.revenue?.toLocaleString('es-EC', { minimumFractionDigits: 2 }) || '0'}`, color: '#4cc9f0' },
          { label: 'Costo total',     valor: `$${kpis?.costo?.toLocaleString('es-EC',   { minimumFractionDigits: 2 }) || '0'}`, color: '#e63946' },
          { label: 'Margen bruto',    valor: `$${kpis?.margen?.toLocaleString('es-EC',  { minimumFractionDigits: 2 }) || '0'}`, color: '#4caf50' },
          { label: 'Margen global %', valor: `${kpis?.margen_pct || 0}%`, color: colorMargen(kpis?.margen_pct || 0) },
        ].map((k, i) => (
          <div key={i} style={{ ...s.kpiRent, borderTopColor: k.color }}>
            <div style={{ color: k.color, fontSize: 20, fontWeight: 'bold', marginBottom: 4 }}>{k.valor}</div>
            <div style={{ color: '#aaa', fontSize: 11 }}>{k.label}</div>
          </div>
        ))}
      </div>

      {/* Tabs categoría */}
      <div style={s.tabs}>
        {CATEGORIAS.map(c => (
          <button key={c.key}
            style={{ ...s.tab, ...(categoriaActiva === c.key ? { ...s.tabActivo, borderBottomColor: c.color } : {}) }}
            onClick={() => setCategoriaActiva(c.key)}>
            {c.label}
            {datos[c.key] && (
              <span style={{ ...s.tabBadge, background: categoriaActiva === c.key ? c.color : '#2d2d44' }}>
                {datos[c.key].productos.length}
              </span>
            )}
          </button>
        ))}
      </div>

      <div style={{ ...s.contenido, borderRadius: '0 0 12px 12px' }}>
        {kpisCat && (
          <div style={{ display: 'flex', gap: 10, marginBottom: 20, padding: '14px 16px', background: '#0f0f1a', borderRadius: 8 }}>
            <div style={{ flex: 1, textAlign: 'center' }}>
              <div style={{ color: cat.color, fontSize: 16, fontWeight: 'bold' }}>
                ${kpisCat.revenue.toLocaleString('es-EC', { minimumFractionDigits: 2 })}
              </div>
              <div style={{ color: '#555', fontSize: 11 }}>Revenue {cat.label.split(' ')[1]}</div>
            </div>
            <div style={{ flex: 1, textAlign: 'center' }}>
              <div style={{ color: '#e63946', fontSize: 16, fontWeight: 'bold' }}>
                ${kpisCat.costo.toLocaleString('es-EC', { minimumFractionDigits: 2 })}
              </div>
              <div style={{ color: '#555', fontSize: 11 }}>Costo total</div>
            </div>
            <div style={{ flex: 1, textAlign: 'center' }}>
              <div style={{ color: '#4caf50', fontSize: 16, fontWeight: 'bold' }}>
                ${kpisCat.margen.toLocaleString('es-EC', { minimumFractionDigits: 2 })}
              </div>
              <div style={{ color: '#555', fontSize: 11 }}>Margen bruto</div>
            </div>
            <div style={{ flex: 1, textAlign: 'center' }}>
              <div style={{ color: colorMargen(kpisCat.margen_pct), fontSize: 16, fontWeight: 'bold' }}>
                {kpisCat.margen_pct}%
              </div>
              <div style={{ color: '#555', fontSize: 11 }}>Margen %</div>
            </div>
          </div>
        )}

        {productos.length === 0 ? (
          <p style={{ color: '#555', textAlign: 'center', padding: 24 }}>Sin datos para este período</p>
        ) : (
          <div style={s.tabla}>
            <div style={{ ...s.tablaHeader, gap: 8 }}>
              <span style={{ flex: 3 }}>PRODUCTO</span>
              <span style={{ width: 70,  textAlign: 'right' }}>UNID.</span>
              <span style={{ width: 90,  textAlign: 'right' }}>PVP PROM.</span>
              <span style={{ width: 80,  textAlign: 'right' }}>COSTO U.</span>
              <span style={{ width: 110, textAlign: 'right' }}>REVENUE</span>
              <span style={{ width: 110, textAlign: 'right' }}>COSTO TOTAL</span>
              <span style={{ width: 110, textAlign: 'right' }}>MARGEN $</span>
              <span style={{ width: 120, textAlign: 'center' }}>MARGEN %</span>
            </div>

            {productos.map((p, i) => {
              const mc = colorMargen(p.margen_pct);
              const mb = bgMargen(p.margen_pct);
              return (
                <div key={i} style={{
                  ...s.tablaRowRent,
                  background: i % 2 === 0 ? '#1e1e2e' : '#1a1a2e',
                }}
                  onMouseEnter={e => e.currentTarget.style.background = '#2d2d44'}
                  onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? '#1e1e2e' : '#1a1a2e'}>
                  <div style={{ flex: 3 }}>
                    <div style={{ color: '#fff', fontSize: 12, fontWeight: '600', marginBottom: 2 }}>{p.item}</div>
                    <div style={{ color: '#555', fontSize: 11 }}>{p.codigo} · {p.marca}</div>
                  </div>
                  <span style={{ width: 70, textAlign: 'right', color: '#aaa', fontSize: 13 }}>
                    {p.unidades_vendidas.toFixed(0)}
                  </span>
                  <span style={{ width: 90, textAlign: 'right', color: '#4cc9f0', fontSize: 13 }}>
                    ${p.precio_venta_prom.toFixed(2)}
                  </span>
                  <span style={{ width: 80, textAlign: 'right', color: '#888', fontSize: 13 }}>
                    ${p.costo_unitario.toFixed(2)}
                  </span>
                  <span style={{ width: 110, textAlign: 'right', color: '#4cc9f0', fontWeight: 'bold', fontSize: 13 }}>
                    ${p.revenue_total.toLocaleString('es-EC', { minimumFractionDigits: 2 })}
                  </span>
                  <span style={{ width: 110, textAlign: 'right', color: '#888', fontSize: 13 }}>
                    ${p.costo_total.toLocaleString('es-EC', { minimumFractionDigits: 2 })}
                  </span>
                  <span style={{ width: 110, textAlign: 'right', color: mc, fontWeight: 'bold', fontSize: 13 }}>
                    ${p.margen_bruto.toLocaleString('es-EC', { minimumFractionDigits: 2 })}
                  </span>
                  <div style={{ width: 120, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{ flex: 1, background: '#2a2a3e', borderRadius: 4, height: 6, marginRight: 8, overflow: 'hidden' }}>
                      <div style={{
                        height: 6, borderRadius: 4,
                        width: `${Math.min(Math.max(p.margen_pct, 0), 100)}%`,
                        background: mc, transition: 'width 0.3s',
                      }} />
                    </div>
                    <span style={{
                      background: mb, color: mc,
                      borderRadius: 6, padding: '2px 8px',
                      fontSize: 11, fontWeight: 'bold', minWidth: 42, textAlign: 'center',
                    }}>
                      {p.margen_pct.toFixed(1)}%
                    </span>
                  </div>
                </div>
              );
            })}

            {kpisCat && (
              <div style={{ display: 'flex', gap: 8, padding: '12px 16px', background: '#0f0f1a', borderTop: '2px solid #2d2d44', alignItems: 'center' }}>
                <span style={{ flex: 3, color: '#fff', fontWeight: 'bold', fontSize: 12 }}>
                  TOTAL — {productos.length} productos
                </span>
                <span style={{ width: 70,  textAlign: 'right', color: '#555' }}></span>
                <span style={{ width: 90,  textAlign: 'right', color: '#555' }}></span>
                <span style={{ width: 80,  textAlign: 'right', color: '#555' }}></span>
                <span style={{ width: 110, textAlign: 'right', color: '#4cc9f0', fontWeight: 'bold' }}>
                  ${kpisCat.revenue.toLocaleString('es-EC', { minimumFractionDigits: 2 })}
                </span>
                <span style={{ width: 110, textAlign: 'right', color: '#888', fontWeight: 'bold' }}>
                  ${kpisCat.costo.toLocaleString('es-EC', { minimumFractionDigits: 2 })}
                </span>
                <span style={{ width: 110, textAlign: 'right', color: '#4caf50', fontWeight: 'bold' }}>
                  ${kpisCat.margen.toLocaleString('es-EC', { minimumFractionDigits: 2 })}
                </span>
                <div style={{ width: 120, textAlign: 'center' }}>
                  <span style={{
                    background: bgMargen(kpisCat.margen_pct),
                    color: colorMargen(kpisCat.margen_pct),
                    borderRadius: 6, padding: '3px 10px',
                    fontSize: 12, fontWeight: 'bold',
                  }}>
                    {kpisCat.margen_pct.toFixed(1)}%
                  </span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Componente principal ───
export default function Productos() {
  const hoy    = new Date();
  const hace30 = new Date(hoy); hace30.setDate(hoy.getDate() - 30);
  const fmt    = (d) => d.toISOString().split('T')[0];

  const [fechaInicio,       setFechaInicio]       = useState(fmt(hace30));
  const [fechaFin,          setFechaFin]           = useState(fmt(hoy));
  const [datosVentas,       setDatosVentas]        = useState(null);
  const [datosCompras,      setDatosCompras]       = useState(null);
  const [datosRentabilidad, setDatosRentabilidad]  = useState(null);
  const [cargando,          setCargando]           = useState(true);
  const [cargandoRent,      setCargandoRent]       = useState(false);
  const [categoriaActiva,   setCategoriaActiva]    = useState('filtros');
  const [tabActivo,         setTabActivo]          = useState('ventas');
  // 'ventas' | 'compras' | 'rentabilidad' | 'agrupamiento'

  useEffect(() => { cargar(); }, []); // eslint-disable-line

  const cargar = async (fi = fechaInicio, ff = fechaFin) => {
    setCargando(true);
    try {
      const [ventas, compras] = await Promise.all([
        api.get(`/productos/top?fecha_inicio=${fi}&fecha_fin=${ff}`),
        api.get(`/productos/compras?fecha_inicio=${fi}&fecha_fin=${ff}`),
      ]);
      setDatosVentas(ventas.error   ? null : ventas);
      setDatosCompras(compras.error ? null : compras);
    } catch {
      setDatosVentas(null);
      setDatosCompras(null);
    } finally {
      setCargando(false);
    }
  };

  const cargarRentabilidad = async (fi = fechaInicio, ff = fechaFin) => {
    setCargandoRent(true);
    try {
      const data = await api.get(`/productos/rentabilidad?fecha_inicio=${fi}&fecha_fin=${ff}`);
      setDatosRentabilidad(data.error ? null : data);
    } catch {
      setDatosRentabilidad(null);
    } finally {
      setCargandoRent(false);
    }
  };

  const aplicarFiltro = () => {
    cargar(fechaInicio, fechaFin);
    if (tabActivo === 'rentabilidad') cargarRentabilidad(fechaInicio, fechaFin);
  };

  const ultimos30 = () => {
    const fi = fmt(hace30);
    const ff = fmt(hoy);
    setFechaInicio(fi);
    setFechaFin(ff);
    cargar(fi, ff);
    if (tabActivo === 'rentabilidad') cargarRentabilidad(fi, ff);
  };

  const handleTabRentabilidad = () => {
    setTabActivo('rentabilidad');
    if (!datosRentabilidad && !cargandoRent) {
      cargarRentabilidad(fechaInicio, fechaFin);
    }
  };

  const datosActivos    = tabActivo === 'ventas' ? datosVentas : datosCompras;
  const categoriaActual = CATEGORIAS.find(c => c.key === categoriaActiva);

  return (
    <div>
      {/* Header */}
      <div style={s.headerRow}>
        <h2 style={s.titulo}>📦 Productos</h2>
        {/* Ocultar filtros de fecha en el tab de agrupamiento */}
        {tabActivo !== 'agrupamiento' && (
          <div style={s.filtrosRow}>
            <button style={s.botonRapido} onClick={ultimos30}>Últimos 30 días</button>
            <input type="date" style={s.inputFecha} value={fechaInicio}
              onChange={e => setFechaInicio(e.target.value)} />
            <span style={{ color: '#aaa' }}>→</span>
            <input type="date" style={s.inputFecha} value={fechaFin}
              onChange={e => setFechaFin(e.target.value)} />
            <button style={s.botonAplicar} onClick={aplicarFiltro}>Aplicar</button>
          </div>
        )}
      </div>

      {datosVentas && tabActivo !== 'agrupamiento' && (
        <p style={s.periodoTxt}>📅 {datosVentas.fecha_inicio} — {datosVentas.fecha_fin}</p>
      )}

      {/* Tabs principales */}
      <div style={s.tabsMain}>
        <button
          style={{ ...s.tabMain, ...(tabActivo === 'ventas' ? s.tabMainActivo : {}) }}
          onClick={() => setTabActivo('ventas')}>
          📈 Ventas
        </button>
        <button
          style={{ ...s.tabMain, ...(tabActivo === 'compras' ? { ...s.tabMainActivo, borderBottomColor: '#f4a261' } : {}) }}
          onClick={() => setTabActivo('compras')}>
          📥 Compras
        </button>
        <button
          style={{ ...s.tabMain, ...(tabActivo === 'rentabilidad' ? { ...s.tabMainActivo, borderBottomColor: '#4caf50' } : {}) }}
          onClick={handleTabRentabilidad}>
          💰 Rentabilidad
        </button>
        {/* ── NUEVO TAB ── */}
        <button
          style={{ ...s.tabMain, ...(tabActivo === 'agrupamiento' ? { ...s.tabMainActivo, borderBottomColor: '#4cc9f0' } : {}) }}
          onClick={() => setTabActivo('agrupamiento')}>
          🗂️ Agrupamiento
        </button>
        <button
          style={{ ...s.tabMain, ...(tabActivo === 'equivalencias' ? { ...s.tabMainActivo, borderBottomColor: '#a855f7' } : {}) }}
          onClick={() => setTabActivo('equivalencias')}>
          🔀 Equivalencias
        </button>
        <button
          style={{ ...s.tabMain, ...(tabActivo === 'optimizacion' ? { ...s.tabMainActivo, borderBottomColor: '#f72585' } : {}) }}
          onClick={() => setTabActivo('optimizacion')}>
          🤖 Optimización
        </button>
      </div>

      {/* ── Tab Equivalencias ── */}
      {tabActivo === 'equivalencias' ? (
        <div style={{ paddingTop: 16 }}>
          <TabEquivalencias />
        </div>
      ) : tabActivo === 'agrupamiento' ? (
        <div style={{ paddingTop: 16 }}>
          <TabAgrupamiento />
        </div>
      ) : tabActivo === 'optimizacion' ? (
        <div style={{ paddingTop: 16 }}>
          <TabOptimizacion />
        </div>
      ) : tabActivo === 'rentabilidad' ? (
        <div style={{ paddingTop: 16 }}>
          {cargandoRent ? (
            <p style={{ color: '#aaa', padding: 40, textAlign: 'center' }}>Calculando márgenes...</p>
          ) : !datosRentabilidad ? (
            <p style={{ color: '#e63946', padding: 40, textAlign: 'center' }}>No se pudo cargar la rentabilidad</p>
          ) : (
            <TablaRentabilidad
              datos={datosRentabilidad}
              categoriaActiva={categoriaActiva}
              setCategoriaActiva={setCategoriaActiva}
            />
          )}
        </div>
      ) : (
        <>
          {/* Tabs de categorías — ventas y compras */}
          <div style={s.tabs}>
            {CATEGORIAS.map(c => (
              <button key={c.key}
                style={{ ...s.tab, ...(categoriaActiva === c.key ? { ...s.tabActivo, borderBottomColor: c.color } : {}) }}
                onClick={() => setCategoriaActiva(c.key)}>
                {c.label}
                {datosActivos && (
                  <span style={{ ...s.tabBadge, background: categoriaActiva === c.key ? c.color : '#2d2d44' }}>
                    {datosActivos[c.key]?.productos.length || 0}
                  </span>
                )}
              </button>
            ))}
          </div>

          <div style={s.contenido}>
            {cargando ? (
              <p style={{ color: '#aaa', padding: 40, textAlign: 'center' }}>Cargando datos...</p>
            ) : !datosActivos ? (
              <p style={{ color: '#e63946', padding: 40, textAlign: 'center' }}>No se pudo cargar</p>
            ) : (
              <TablaTop
                datos={datosActivos[categoriaActiva]}
                color={categoriaActual.color}
                esCompra={tabActivo === 'compras'}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
}

const s = {
  titulo:        { color: '#fff', margin: 0 },
  headerRow:     { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, flexWrap: 'wrap', gap: 16 },
  filtrosRow:    { display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' },
  botonRapido:   { background: '#2d2d44', border: 'none', borderRadius: 6, color: '#aaa', padding: '8px 14px', cursor: 'pointer', fontSize: 13 },
  inputFecha:    { background: '#1e1e2e', border: '1px solid #2d2d44', borderRadius: 6, color: '#fff', padding: '8px 12px', fontSize: 13 },
  botonAplicar:  { background: '#e63946', border: 'none', borderRadius: 6, color: '#fff', padding: '8px 16px', cursor: 'pointer', fontWeight: 'bold', fontSize: 13 },
  periodoTxt:    { color: '#888', fontSize: 12, marginBottom: 8 },
  tabsMain:      { display: 'flex', gap: 4, marginBottom: 0, borderBottom: '2px solid #2d2d44' },
  tabMain:       { background: 'none', border: 'none', borderBottom: '3px solid transparent', color: '#888', padding: '12px 24px', cursor: 'pointer', fontSize: 15, fontWeight: '500', marginBottom: '-2px' },
  tabMainActivo: { color: '#fff', borderBottomColor: '#4cc9f0', fontWeight: 'bold' },
  tabs:          { display: 'flex', gap: 4, marginBottom: 0, borderBottom: '1px solid #2d2d44', marginTop: 4 },
  tab:           { background: 'none', border: 'none', borderBottom: '2px solid transparent', color: '#888', padding: '10px 16px', cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 },
  tabActivo:     { color: '#fff', fontWeight: 'bold' },
  tabBadge:      { fontSize: 10, borderRadius: 10, padding: '2px 6px', color: '#fff' },
  contenido:     { background: '#1e1e2e', borderRadius: '0 0 12px 12px', padding: 24, minHeight: 300 },
  marcaLider:    { color: '#aaa', fontSize: 13, marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  marcaTotal:    { color: '#555', fontSize: 12 },
  tabla:         { marginTop: 24, borderRadius: 8, overflow: 'hidden' },
  tablaHeader:   { display: 'flex', color: '#555', fontSize: 11, fontWeight: 'bold', padding: '10px 16px', background: '#0f0f1a', gap: 8, borderBottom: '1px solid #2d2d44' },
  tablaRow:      { display: 'flex', padding: '12px 0', borderBottom: '1px solid #1a1a2e', gap: 8, alignItems: 'center' },
  tablaRowRent:  { display: 'flex', padding: '11px 16px', gap: 8, alignItems: 'center', transition: 'background 0.15s', cursor: 'default' },
  itemNombre:    { color: '#fff', fontSize: 13, marginBottom: 2 },
  itemCodigo:    { color: '#555', fontSize: 11 },
  barraContainer:{ background: '#2a2a3e', borderRadius: 3, height: 4, marginBottom: 4 },
  barraFill:     { height: 4, borderRadius: 3 },
  kpiRent:       { background: '#1e1e2e', borderRadius: 10, padding: '16px 20px', flex: 1, minWidth: 140, borderTop: '3px solid' },
};